import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './index.css';

import {
  TyC,
  FAQ,
  Feed,
  Home,
  Mision,
  Us,
  Login,
  MyAccount,
  MyProfile,
  MyPublications,
  Favorites,
  PrivacyPolicy,
  Signup,
  Publication,
  Publish,
  ForgotPassword,
  ResetPassword,
  PetsMap,
  SolvedCases,
  MyPets,
  CreatePetProfile,
  PublicPetProfile,
  SearchByPhoto,
  Impacto,
} from '@/routes';
import { SENTRY_DSN } from '@/constants';
import { BaseLayout } from '@/components/Layouts/BaseLayout';
import { FiltersProvider } from '@/hooks/FiltersContext';
import { SidebarProvider } from '@/components/ui/sidebar';
import { LostAndFoundSkeleton, MapSkeleton, SolvedCasesSkeleton } from '@/components/Skeletons';
import {
  lostPetsLoader,
  foundPetsLoader,
  mapPetsLoader,
  solvedCasesLoader,
} from '@/loaders/petsLoader';

import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [Sentry.replayIntegration()],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.VITE_ENV,
});

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <QueryClientProvider client={queryClient}>
        <FiltersProvider>
          <SidebarProvider defaultOpen={false}>
            <div className="w-full">
              <BaseLayout />
            </div>
          </SidebarProvider>
        </FiltersProvider>
      </QueryClientProvider>
    ),
    errorElement: (
      <Sentry.ErrorBoundary>
        <div className="p-10 text-center">💥 Algo falló en la UI</div>
      </Sentry.ErrorBoundary>
    ),
    children: [
      { index: true, element: <Home /> },
      {
        path: 'lost',
        element: <Feed status="lost" />,
        loader: lostPetsLoader,
        hydrateFallbackElement: <LostAndFoundSkeleton />,
      },
      {
        path: 'found',
        element: <Feed status="found" />,
        loader: foundPetsLoader,
        hydrateFallbackElement: <LostAndFoundSkeleton />,
      },
      { path: 'mision', element: <Mision /> },
      { path: 'faq', element: <FAQ /> },
      { path: 'us', element: <Us /> },
      { path: 'publish', element: <Publish /> },
      { path: 'login', element: <Login /> },
      { path: 'signup', element: <Signup /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'reset-password', element: <ResetPassword /> },
      { path: 'my-account', element: <MyAccount /> },
      { path: 'my-profile', element: <MyProfile /> },
      { path: 'my-publications', element: <MyPublications /> },
      { path: 'my-favorites', element: <Favorites /> },
      { path: 'my-pets', element: <MyPets /> },
      { path: 'my-pets/new', element: <CreatePetProfile /> },
      { path: 'my-pets/:id/edit', element: <CreatePetProfile /> },
      { path: 'search-by-photo', element: <SearchByPhoto /> },
      {
        path: 'map',
        element: <PetsMap />,
        loader: mapPetsLoader,
        hydrateFallbackElement: <MapSkeleton />,
      },
      {
        path: 'solved-cases',
        element: <SolvedCases />,
        loader: solvedCasesLoader,
        hydrateFallbackElement: <SolvedCasesSkeleton />,
      },
      { path: 'impacto', element: <Impacto /> },
      { path: 'pet/:id', element: <Publication /> },
      { path: 'pet-profile/:uuid', element: <PublicPetProfile /> },
      { path: 'tyc', element: <TyC /> },
      { path: 'privacy-policy', element: <PrivacyPolicy /> },
      {
        path: '*',
        element: <div className="flex flex-row justify-center">Ruta inexistente</div>,
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
